import { lazy, StrictMode, Suspense } from "react";
import { Redirect } from "react-router";
import {
    BrowserRouter,
    Route,
    type RouteComponentProps,
    useLocation,
} from "react-router-dom";
import { SWRConfig } from "swr";
import { useIsSwitchingAccount } from "./auth/accessToken/switchingAccount";
import { NoticeProvider } from "./common/components/NoticeBanner/FullContext";
import Main from "./components/Main";
import { AppLoading, PageLoading } from "./components/Placeholders/App";
import Root from "./components/Root";
import EnvPatcher from "./components/TopBar/EnvPatcher";
import { PUBLIC_URL } from "./env";
import { WithErrorHandler } from "./errors";
import "./i18n";
import "./sentry";

const AppBar = lazy(() => import("./AppBar"));
const loadAppRoutes = () => import("./AppRoutes");
const AppRoutes = lazy(loadAppRoutes);
const loadDashboard = () => import("./dashboard/components/Dashboard");
const RedirectToPullRequestRoute = lazy(
    () => import("./RedirectToPullRequestRoute"),
);
const MobileMenu = lazy(() => import("./components/MobileMenu/MobileMenu"));
const TopBar = lazy(() => import("./components/TopBar/TopBar"));
const AccountIdChangedDispatcher = lazy(() =>
    import("./auth/useAccountId").then((module) => ({
        default: module.AccountIdChangedDispatcher,
    })),
);
const Allow = lazy(() => import("./auth/accessToken/components/Allow"));
const RedirectToAccount = lazy(
    () => import("./auth/accessToken/components/RedirectToAccount"),
);
const WithAccountAccessToken = lazy(
    () => import("./auth/accessToken/components/WithAccountAccessToken"),
);
const Welcome = lazy(() => import("./welcome/modal/components/Welcome"));
const CommandPalette = lazy(
    () => import("./components/CommandPalette/CommandPalette"),
);
const InstallShopify = lazy(() =>
    import("./callbacks/components/InstallShopify").then((module) => ({
        default: module.InstallShopify,
    })),
);
const PostHogWrapper = lazy(() =>
    import("./posthog/PostHogWrapper").then((module) => ({
        default: module.PostHogWrapper,
    })),
);

// Routes outside an account, like the Shopify install callback, have no app
// bar or top bar, so no app shell while they load. Matches the EXCEMPTIONS
// in WithAccountAccessToken.
const ROUTES_WITHOUT_APP = ["/shopify-install"];

const AppFallback = () => {
    const { pathname } = useLocation();
    return ROUTES_WITHOUT_APP.includes(pathname) ? null : <AppLoading />;
};

// While switching account the app bar and top bar stay, only the page
// content waits on the new account
const AccountRoutes = () => {
    const switching = useIsSwitchingAccount();
    if (switching) {
        return <PageLoading />;
    }
    return (
        <Suspense fallback={<PageLoading />}>
            <AppRoutes />
        </Suspense>
    );
};

// The app inside an account
const AccountApp = (props: RouteComponentProps<{ accountId: string }>) => {
    // Load the page's code while the account's tokens load, not after. The
    // route also matches other top level paths (e.g. /shopify-install).
    if (/^[PT]\d{8}$/.test(props.match.params.accountId)) {
        loadAppRoutes();
        if (props.match.isExact) {
            // The dashboard is the account's index page
            loadDashboard();
        }
    }
    return (
        <>
            <AccountIdChangedDispatcher />
            <WithAccountAccessToken {...props}>
                <NoticeProvider>
                    <EnvPatcher>
                        <AppBar />
                        <TopBar />
                        <MobileMenu />
                        <Main>
                            <AccountRoutes />
                            <Allow anyOfScopes={[["admin:accounts"]]}>
                                <Welcome />
                            </Allow>
                            <CommandPalette />
                        </Main>
                    </EnvPatcher>
                </NoticeProvider>
            </WithAccountAccessToken>
        </>
    );
};

// Setting basename in BrowserRouter so that pull requests can be served from a relative path
const basename = PUBLIC_URL ? PUBLIC_URL : undefined;

function App() {
    return (
        <StrictMode>
            <Suspense fallback={null}>
                <SWRConfig
                    value={{
                        revalidateOnFocus: false,
                        revalidateOnReconnect: false,
                        revalidateOnMount: true,
                        shouldRetryOnError: false,
                        provider: () => new Map(),
                    }}
                >
                    <Root>
                        <BrowserRouter basename={basename}>
                            {/* Inside the router, the fallback reads the location */}
                            <Suspense fallback={<AppFallback />}>
                                <PostHogWrapper>
                                    <WithErrorHandler>
                                        <Route
                                            path={"/_pr"}
                                            component={
                                                RedirectToPullRequestRoute
                                            }
                                        />
                                        <Route
                                            path={"/"}
                                            exact
                                            component={RedirectToAccount}
                                        />
                                        <Route
                                            path={"/login"}
                                            render={() => <Redirect to="/" />}
                                        />
                                        <Route
                                            path="/shopify-install"
                                            exact
                                            component={InstallShopify}
                                        />
                                        <Route
                                            path={`/:accountId`}
                                            render={(props) => (
                                                <AccountApp {...props} />
                                            )}
                                        />
                                    </WithErrorHandler>
                                </PostHogWrapper>
                            </Suspense>
                        </BrowserRouter>
                    </Root>
                </SWRConfig>
            </Suspense>
        </StrictMode>
    );
}

export default App;
